<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="position">
            {{ position.terminal_value }}
        </template>

        <template v-slot:header v-if="position">
            <div class="row mb-0">
                <div class="col">
                    <span class="text-muted">
                        {{ position.company.name }}
                    </span>
                </div>
            </div>
            <div v-if="!position.is_active" class="row mb-0">
                <div class="col">
                    <span class="text-danger-dark text-bold text-italic">
                        Inactive
                    </span>
                </div>
            </div>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabPositionContentMain" id="tabPositionLinkMain" data-toggle="tab" role="tab" aria-controls="tabPositionContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot v-if="position">
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabPositionContentMain" role="tabpanel" aria-labelledby="tabPositionLinkMain">
                    <!-- Position information widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Position Information</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositionInfo">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ position.num_employees }}
                                    </span>
                                    <label>Number of Active Employees</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ position.paygroup.name }}
                                    </span>
                                    <label>Pay Group</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ position.pos_value }}
                                    </span>
                                    <label>POS Value</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ position.soc_code }}
                                    </span>
                                    <label>SOC Code</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ position.id }}
                                    </span>
                                    <label>Position ID</label>
                                </div>

                                <div class="mui-textfield">
                                    <span class="textfield">
                                        <span v-if="position.bank_account_net_pay_id">{{ position.bank_account_net_pay.name }}</span>
                                        <i v-else>Default</i>
                                    </span>
                                    <label>Net Pay Bank Account</label>
                                </div>

                                <div class="mui-textfield">
                                    <span class="textfield">
                                        <span v-if="position.bank_account_taxes_id">{{ position.bank_account_taxes.name }}</span>
                                        <i v-else>Default</i>
                                    </span>
                                    <label>Taxes Bank Account</label>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Earnings widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Earnings ({{ position.earning_types?.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editEarnings">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="position.earning_types?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th class="text-right">Type</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="earning_type in position.earning_types">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ earning_type.earning_type.name }}</span>
                                            </td>
                                            <td class="text-right">
                                                <span>{{ earning_type.earning_type.current_version.type_pretty }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="info-widget-empty p-2">
                                This position is not associated with any earnings.
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Deductions widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Deductions ({{ position.deduction_types?.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editDeductions">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="position.deduction_types?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="deduction_type in position.deduction_types">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ deduction_type.deduction_type.name }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="info-widget-empty p-2">
                                This position is not associated with any deductions.
                            </div>
                        </slot>
                    </info-widget>

                    <button-row stacked class="pt-0">
                        <button v-if="position.is_active" class="btn btn-outline-primary" @click.prevent="deactivate()">
                            Deactivate
                        </button>
                        <button v-else class="btn btn-outline-primary" @click.prevent="reactivate()">
                            Reactivate
                        </button>
                    </button-row>
                </div>
            </div>
        </slot>

        <edit-position-modal
            v-if="position && editPositionModalOpen"
            ref="editPositionModal"
            :position="position"
            :client="client"
            :editMode="editMode"
            @updated="onUpdate"
            @close="editPositionModalOpen = false; editMode = ''"
        />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditPositionModal from './EditPositionModal'

export default {
    props: ['positionId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditPositionModal, },
    computed: {},
    data() {

        return {
            position: null,
            activeTab: this.tab || 'main',
            editPositionModalOpen: false,
            editMode: 'info',
        }
    },
    watch: {
        positionId() {
            this.getPosition()
        }
    },
    mounted() {
        this.getPosition()
    },
    methods: {
        getPosition() {
            this.position = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/positions/${this.positionId}`).then(resp => {
                this.position = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editPositionInfo() {
            this.editPosition('info')
        },
        editEarnings() {
            this.editPosition('earnings')
        },
        editDeductions() {
            this.editPosition('deductions')
        },
        editPosition(editMode) {
            this.editMode = editMode
            this.editPositionModalOpen = true
        },
        onUpdate(position) {
            if (position) {
                Object.assign(this.position, position)
            }
            this.$emit('updated')
        },
        deactivate() {
            this.$api.patch(`/clients/${this.client.id}/settings/positions/${this.positionId}`, {action: 'deactivate'}).then(resp => {
                Object.assign(this.position, resp)
                this.$emit('updated')
            })
        },
        reactivate() {
            this.$api.patch(`/clients/${this.client.id}/settings/positions/${this.positionId}`, {action: 'reactivate'}).then(resp => {
                Object.assign(this.position, resp)
                this.$emit('updated')
            })
        }
    },
}
</script>

